import type { Observation } from '@medplum/fhirtypes';
import React from 'react';

import { Grid, Icon, Stack, Tag, Text } from '@eluve/components';
import { type FhirPageControls, LoadMoreRow } from '@eluve/fhir-data';
import {
  EM_DASH,
  composedObservationValue,
  formatChoiceValue,
  getChoiceValue,
  observationDateText,
  resolveObservationPresentation,
} from '@eluve/fhir-renderer';

import {
  type GroupedObservations,
  type ObservationGroup,
  formatMeasureValue,
} from '../measures';

import { TrendSparkline } from './TrendSparkline';

/**
 * The Overview-tab Observations card (ENG-5366): catalog measures first —
 * grouped rows with latest/first values and a sparkline — then the
 * non-catalog observations as a reverse-chronological list. Rows are
 * composed from renderer primitives (value/presentation helpers,
 * `observationDateText`); interactivity and badging are app chrome, per the
 * renderer's presentational contract.
 */
export type ObservationsCardProps = {
  grouped: GroupedObservations;
  loading: boolean;
  failed: boolean;
  paging: FhirPageControls;
  /** Open the detail drawer for a group (routing owned by the section). */
  onOpenGroup: (token: string) => void;
};

const CenteredNote: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Grid place="center" className="px-6 py-12">
    <Text size="sm" color="tertiary" weight="medium">
      {children}
    </Text>
  </Grid>
);

const MeasureRow: React.FC<{
  group: ObservationGroup;
  onOpen: () => void;
}> = ({ group, onOpen }) => {
  const hasPending = group.pending.length > 0;
  const latestPending = hasPending
    ? group.pending[group.pending.length - 1]
    : undefined;
  const hasAmended = group.series.some((point) => point.amended);
  const first = group.series[0];
  const last = group.series[group.series.length - 1];
  const recordedDate = observationDateText(last?.observation ?? group.latest);

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`measure-row-${group.measure?.key ?? group.token}`}
      className="relative flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-left not-first:before:absolute not-first:before:inset-x-1 not-first:before:top-0 not-first:before:border-t not-first:before:border-border-primary hover:bg-bg-secondary not-first:hover:before:opacity-0 [:hover+&]:before:opacity-0"
    >
      <div className="min-w-0 flex-1">
        <Stack direction="horizontal" gap={2} align="center" fullWidth={false}>
          <Text inline size="sm" weight="medium" className="truncate">
            {group.label}
          </Text>
          {hasPending && (
            <Tag pill subtle size="xs" color="insights" label="Draft" />
          )}
          {!hasPending && hasAmended && (
            <Tag pill subtle size="xs" color="warning" label="Amended" />
          )}
        </Stack>
        <Text size="xs" color="tertiary" weight="medium" className="mt-0.5">
          {[
            recordedDate ? `Recorded: ${recordedDate}` : undefined,
            group.method ? `Method: ${group.method}` : undefined,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        {first && last && group.series.length > 1 ? (
          <div className="grid grid-cols-[auto_1.5rem_auto] items-baseline text-sm tabular-nums">
            <Text inline size="sm" color="tertiary" className="text-center">
              {formatMeasureValue(first.value, group.unit)}
            </Text>
            <span className="text-center text-text-tertiary opacity-60">→</span>
            <Text inline size="sm" weight="semibold" className="text-center">
              {formatMeasureValue(last.value, group.unit)}
            </Text>
          </div>
        ) : last ? (
          <Text inline size="sm" weight="semibold" className="tabular-nums">
            {formatMeasureValue(last.value, group.unit)}
          </Text>
        ) : null}
        <TrendSparkline
          className="text-text-positive"
          points={group.series.map((point) => ({
            value: point.value,
            amended: point.amended,
          }))}
          proposedValue={latestPending?.valueQuantity?.value ?? undefined}
        />
      </div>
      <Icon
        name="ChevronRight"
        size="xs"
        className="shrink-0 text-text-tertiary"
      />
    </button>
  );
};

function observationValueText(observation: Observation): string {
  const presentation = resolveObservationPresentation(observation);
  if (presentation) {
    const composed = composedObservationValue(observation, presentation);
    if (composed) return composed;
  }
  const choice = getChoiceValue(observation, 'value');
  return choice && choice.type !== 'absent'
    ? formatChoiceValue(choice)
    : EM_DASH;
}

const OtherObservationRows: React.FC<{
  groups: ObservationGroup[];
  onOpenGroup: (token: string) => void;
}> = ({ groups, onOpenGroup }) => (
  <>
    {groups.map((group) => (
      <button
        key={group.token}
        type="button"
        onClick={() => onOpenGroup(group.token)}
        data-testid="other-observation-row"
        className="relative flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-left not-first:before:absolute not-first:before:inset-x-1 not-first:before:top-0 not-first:before:border-t not-first:before:border-border-primary hover:bg-bg-secondary not-first:hover:before:opacity-0 [:hover+&]:before:opacity-0"
      >
        <div className="min-w-0 flex-1">
          <Stack
            direction="horizontal"
            gap={2}
            align="center"
            fullWidth={false}
            className="min-h-6"
          >
            <Text inline size="sm" weight="medium" className="truncate">
              {group.label}
            </Text>
            {group.pending.length > 0 && (
              <Tag pill subtle size="xs" color="insights" label="Draft" />
            )}
          </Stack>
          <Text size="xs" color="tertiary" weight="medium" className="mt-0.5">
            {observationValueText(group.latest)}
          </Text>
        </div>
        <Icon
          name="ChevronRight"
          size="xs"
          className="shrink-0 text-text-tertiary"
        />
      </button>
    ))}
  </>
);

export const ObservationsCard: React.FC<ObservationsCardProps> = ({
  grouped,
  loading,
  failed,
  paging,
  onOpenGroup,
}) => {
  const isEmpty = grouped.measures.length === 0 && grouped.others.length === 0;

  return (
    <section
      data-testid="observations-card"
      className="rounded-lg bg-bg-primary shadow-xs ring-1 ring-black/5"
    >
      <Stack
        direction="horizontal"
        gap={2}
        align="center"
        justify="between"
        className="h-12 px-4"
      >
        <Text inline size="sm" weight="semibold">
          Observations
        </Text>
      </Stack>

      {failed ? (
        <CenteredNote>Couldn't load observations.</CenteredNote>
      ) : loading ? (
        <CenteredNote>Loading observations…</CenteredNote>
      ) : isEmpty ? (
        <CenteredNote>No observations recorded yet.</CenteredNote>
      ) : (
        <>
          <div className="p-1">
            {grouped.measures.map((group) => (
              <MeasureRow
                key={group.token}
                group={group}
                onOpen={() => onOpenGroup(group.token)}
              />
            ))}
            <OtherObservationRows
              groups={grouped.others}
              onOpenGroup={onOpenGroup}
            />
          </div>
          <LoadMoreRow paging={paging} noun="observations" />
        </>
      )}
    </section>
  );
};
